"use client";

import Image from "next/image";
import Link from "@/components/ui/CcLink";
import {
  ArrowUpRight,
  Bot,
  LayoutTemplate,
  Megaphone,
  Palette,
  Search,
  Target,
  type LucideIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type CSSProperties } from "react";

import { CcSanjayaEyebrow } from "@/components/ui/CcSanjayaEyebrow";
import { CcPillTabList, type CcPillTabItem } from "@/components/ui/CcPillTabList";
import { getServiceDirectoryBrandLogoSrc } from "@/lib/cc-service-directory-brand-icons";
import { getServiceDirectoryLinkIcon } from "@/lib/cc-service-directory-icons";
import {
  filterNavServiceMegaColumns,
  type NavServiceMegaColumn,
} from "@/lib/cc-nav-services-mega";
import { getServicesOverviewPageCopy } from "@/lib/services-overview-page-copy";
import type { Lang } from "@/lib/types";
import { usePathname } from "next/navigation";

const ALL_SERVICES_TAB = "all-services";

const SERVICE_ICONS: Record<string, LucideIcon> = {
  "web-design": LayoutTemplate,
  "lead-gen": Target,
  seo: Search,
  sem: Megaphone,
  "ai-automation": Bot,
  branding: Palette,
};

export type CcServiceDirectoryHeaderCopy = {
  eyebrow: string;
  title: string;
  lead: string;
};

type CcServiceDirectoryProps = {
  columns: NavServiceMegaColumn[];
  lang: Lang;
  /** Override services-overview directory chrome (hub-scoped sub-pages). */
  headerCopy?: CcServiceDirectoryHeaderCopy;
  /** Pill filter tabs; defaults to off when a single column is passed. */
  showFilterTabs?: boolean;
  /** Hide the card for this sub-page key (matches mega menu link `key`). */
  excludeSubKey?: string;
  /** Hide the card matching this path; defaults to the current pathname when omitted. */
  currentPath?: string;
};

function tabFromHash(columns: NavServiceMegaColumn[]) {
  const hash = window.location.hash.slice(1);
  if (hash === ALL_SERVICES_TAB || columns.some((column) => column.tag === hash)) {
    return hash;
  }
  return ALL_SERVICES_TAB;
}

export function CcServiceDirectory({
  columns,
  lang,
  headerCopy,
  showFilterTabs,
  excludeSubKey,
  currentPath,
}: CcServiceDirectoryProps) {
  const pathname = usePathname();
  const overviewDirectory = getServicesOverviewPageCopy(lang).directory;
  const text = headerCopy ?? overviewDirectory;

  const directoryColumns = useMemo(
    () =>
      filterNavServiceMegaColumns(columns, {
        excludeSubKey,
        excludeHref: currentPath ?? pathname ?? undefined,
      }),
    [columns, currentPath, excludeSubKey, pathname],
  );

  const filterTabsEnabled = showFilterTabs ?? directoryColumns.length > 1;
  const [activeTab, setActiveTab] = useState(ALL_SERVICES_TAB);

  useEffect(() => {
    if (!filterTabsEnabled) {
      return;
    }
    const syncFromHash = () => setActiveTab(tabFromHash(directoryColumns));
    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, [directoryColumns, filterTabsEnabled]);

  const visibleColumns = useMemo(
    () =>
      !filterTabsEnabled || activeTab === ALL_SERVICES_TAB
        ? directoryColumns
        : directoryColumns.filter((column) => column.tag === activeTab),
    [activeTab, directoryColumns, filterTabsEnabled],
  );

  const selectTab = useCallback(
    (tag: string) => {
      setActiveTab(tag);
      if (filterTabsEnabled) {
        window.history.replaceState(window.history.state, "", `#${tag}`);
      }
    },
    [filterTabsEnabled],
  );

  const tabItems = useMemo((): CcPillTabItem[] => {
    const items: CcPillTabItem[] = [
      { id: ALL_SERVICES_TAB, label: overviewDirectory.all },
    ];
    for (const column of directoryColumns) {
      items.push({ id: column.tag, label: column.header });
    }
    return items;
  }, [directoryColumns, overviewDirectory.all]);

  if (directoryColumns.length === 0) {
    return null;
  }

  return (
    <section className="cc-service-directory" data-cc-nav="light" aria-labelledby="service-directory-title">
      <div className="cc-service-directory__inner">
        <header className="cc-service-directory__header">
          <CcSanjayaEyebrow theme="on-subtle">
            {text.eyebrow}
          </CcSanjayaEyebrow>
          <h2 id="service-directory-title" className="cc-service-directory__title">
            {text.title}
          </h2>
          <p className="cc-service-directory__lead">{text.lead}</p>
        </header>

        {filterTabsEnabled ? (
          <CcPillTabList
            items={tabItems}
            activeId={activeTab}
            onSelect={selectTab}
            ariaLabel={text.eyebrow}
            tabPanelId="service-directory-panel"
          />
        ) : null}

        <div
          key={activeTab}
          id="service-directory-panel"
          className="cc-service-directory__panel"
          role="tabpanel"
          tabIndex={0}
        >
          {visibleColumns.map((column) => {
            const Icon = SERVICE_ICONS[column.tag] ?? LayoutTemplate;
            return (
              <section key={column.tag} className="cc-service-directory__group">
                <div className="cc-service-directory__group-heading">
                  <span className="cc-service-directory__group-icon" aria-hidden="true">
                    <Icon size={18} strokeWidth={1.8} />
                  </span>
                  <h3>{column.header}</h3>
                  <Link href={column.headerHref} className="cc-service-directory__group-link">
                    {column.viewLabel}
                    <ArrowUpRight size={15} aria-hidden="true" />
                  </Link>
                </div>

                <div className="cc-service-directory__grid">
                  {column.links.map((link, index) => {
                    const LinkIcon = getServiceDirectoryLinkIcon(link.icon);
                    const brandLogoSrc = link.iconBrand
                      ? getServiceDirectoryBrandLogoSrc(link.iconBrand)
                      : null;
                    return (
                      <Link
                        key={link.key}
                        href={link.href}
                        className="cc-service-directory__card"
                        style={{ "--cc-directory-index": index } as CSSProperties}
                      >
                        <span className="cc-service-directory__card-icon" aria-hidden="true">
                          {brandLogoSrc ? (
                            <Image
                              className="cc-service-directory__card-brand"
                              src={brandLogoSrc}
                              alt=""
                              width={20}
                              height={20}
                              unoptimized
                            />
                          ) : (
                            <LinkIcon size={20} strokeWidth={1.6} />
                          )}
                        </span>
                        <span className="cc-service-directory__card-copy">
                          <strong>{link.label}</strong>
                          <span>{link.description}</span>
                        </span>
                        <ArrowUpRight
                          className="cc-service-directory__card-arrow"
                          size={17}
                          aria-hidden="true"
                        />
                      </Link>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      </div>
    </section>
  );
}
